import React, { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useNotification } from '../../context/NotificationContext';
import { useIntegrationsStatusContext } from '../../context/IntegrationsStatusContext';
import type { Integration } from '../../services/integration.types';
import PageLayout from '../common/PageLayout';
import IntegrationsOverview from './IntegrationsOverview';
import { useIntegrationMutations } from './hooks/useIntegrationMutations';

const Integrations: React.FC = () => {
    const { t } = useTranslation();
    const { showNotification } = useNotification();
    const { integrations, isLoading, syncIntegration, isSyncing } = useIntegrationsStatusContext();
    const [deletingIntegrationName, setDeletingIntegrationName] = useState<string | null>(null);

    const {
        deleteIntegration,
        isExchangeCreating,
        isWalletCreating,
        isCsvImporting,
    } = useIntegrationMutations();

    const handleSync = (integration: Integration) => {
        integration.status.status = 'in_progress';
        syncIntegration(
            { jobId: integration.jobId, platformName: integration.platformName },
            {
                onSuccess: () => {},
                onError: () => {
                    showNotification(t('integrations.overview.notifications.syncError'), 'error');
                },
            }
        );
    };

    const handleDelete = async (integration: Integration) => {
        setDeletingIntegrationName(integration.name);
        deleteIntegration(
            {
                name: integration.name,
                jobId: integration.jobId,
                jobTableName: integration.jobTableName,
            },
            {
                onSuccess: () => {
                    setDeletingIntegrationName(null);
                },
                onError: () => {
                    setDeletingIntegrationName(null);
                    showNotification(t('integrations.overview.notifications.deleteError'), 'error');
                },
            }
        );
    };

    const showSkeleton = isExchangeCreating || isWalletCreating || isCsvImporting || isSyncing || (isLoading && integrations.length === 0);

    return (
        <PageLayout title={t('integrations.overview.title')}>
            <IntegrationsOverview
                integrations={integrations}
                onSync={handleSync}
                onDelete={handleDelete}
                deletingIntegrationName={deletingIntegrationName}
                isLoading={showSkeleton} 
            />
        </PageLayout>
    );
};

export default Integrations;
